import { KeyRound, Landmark, ScrollText, ShieldCheck, Siren, UserRoundCheck } from 'lucide-react';

import { Medallion } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';

import type { MedallionHue } from '@/components/bedrock';
import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';

/**
 * The trust hub's "at a glance" cards, split from `TrustHub.tsx` to keep that
 * page under the ~300-line ceiling (`.claude/rules/architecture.md`).
 *
 * The six cards were hand-written and byte-identical apart from their keys, so
 * they are a mapped list here — 57 lines of copy-paste became a six-entry
 * array. Adding a seventh fact is now one line instead of nine, and the seventh
 * cannot arrive with a different card treatment.
 *
 * ## Columns, not a grid
 *
 * The six facts are wildly unequal — one is a single line, another is six — so
 * neither grid answer works. Equal row height boxes the one-liner inside a tall
 * sibling's whitespace, and `items-start` (what this rendered before) leaves the
 * hole beside it instead: measured at 1440, four visible gaps, the worst ~120px
 * under a two-line card next to a six-line one.
 *
 * CSS multi-column is the tool for a list of unequal blocks. The browser
 * balances the two columns by HEIGHT rather than by count, so no arrangement of
 * the six can leave a hole, and `break-inside-avoid` keeps a card whole. The
 * reading order stays the DOM order down column one and then column two, which
 * is what a `<dl>` of independent facts wants — these are not a sequence.
 *
 * ## The medallion earns its place here more than anywhere
 *
 * This is the page a security reviewer is sent to, and six identical grey-
 * eyebrowed white cards give them nothing to navigate by. The hue groups the
 * facts they will be looking for: where the data lives (cyan), who can reach it
 * (blue), what happens when something goes wrong (violet), and what has been
 * certified (purple).
 */
const FACTS: readonly {
  readonly id: string;
  readonly icon: LucideIcon;
  readonly hue: MedallionHue;
}[] = [
  { id: 'region', icon: Landmark, hue: 'cyan' },
  { id: 'encryption', icon: KeyRound, hue: 'blue' },
  { id: 'aiact', icon: ScrollText, hue: 'purple' },
  { id: 'breach', icon: Siren, hue: 'violet' },
  { id: 'dsar', icon: UserRoundCheck, hue: 'blue' },
  { id: 'soc2', icon: ShieldCheck, hue: 'purple' },
];

export function TrustHubGlance(): ReactElement {
  const { t } = useT();
  return (
    <section aria-labelledby="at-a-glance" className="mb-12">
      <h2
        id="at-a-glance"
        className="mb-4 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
      >
        {t('trusthub.glance.heading')}
      </h2>
      <dl className="gap-4 sm:columns-2">
        {FACTS.map((fact) => (
          <div
            key={fact.id}
            className="mb-4 break-inside-avoid rounded-[var(--bd-radius)] border border-[color:var(--bd-line)] bg-gradient-to-b from-[color:var(--bd-raise)] to-[color:var(--bd-page)] p-5"
          >
            {/* The medallion lives INSIDE the `<dt>`, not in a wrapper beside
                it. A `<dl>` permits exactly one `<div>` between itself and a
                `dt`/`dd` pair; a second nesting level makes both orphans, which
                is what the first version of this shipped — axe reported
                `definition-list` + `dlitem` on six cells of the matrix (three
                locales × two viewports) while lint, types and the unit tests
                were all green. Flexing the `<dt>` gets the same row for free. */}
            <dt className="flex items-center gap-3 font-mono text-[10.5px] uppercase tracking-[0.13em] text-[color:var(--bd-faint)] rtl:font-sans rtl:normal-case rtl:tracking-normal">
              <Medallion icon={fact.icon} hue={fact.hue} size="sm" />
              {t(`trusthub.glance.${fact.id}.term`)}
            </dt>
            <dd className="mt-3 text-[0.94rem] leading-[1.6] text-[color:var(--bd-body)]">
              {t(`trusthub.glance.${fact.id}.desc`)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
